/**
 * ChannelBadge - Badge pequeno do canal exibido sobre o avatar de cards/itens
 *
 * Lógica unificada (espelha ConversationList e QueueKanbanCard):
 *   - WhatsApp pessoal (channel_type='whatsapp', provider != WABA) → glifo verde #25D366
 *   - WhatsApp oficial / WABA (channel_type='whatsapp_oficial' OU provider slug
 *     contém meta|waba|cloud) → glifo na cor do foreground (preto/branco)
 *   - Instagram → ícone rosa
 *   - Messenger → ícone azul
 *   - Telegram → ícone sky
 *   - Webchat → ícone globe
 *   - Fallback → ícone Inbox
 *
 * Renderiza um círculo posicionado em -bottom-1 -left-1 (requer wrapper relative).
 */

import { Instagram, MessagesSquare, Send, Globe, Inbox } from 'lucide-react';
import type { JSX } from 'react';

interface ChannelBadgeProps {
  channelType?: string | null;
  providerSlug?: string | null;
}

// Glifo SVG do WhatsApp reutilizado para variantes verde e mono (WABA)
const WhatsAppGlyph = ({ color }: { color: string }) => (
  <svg viewBox="0 0 32 32" className="h-[12px] w-[12px]" aria-hidden="true">
    <path
      fill={color}
      d="M16 2.667C8.636 2.667 2.667 8.636 2.667 16c0 2.355.621 4.66 1.8 6.687L2.667 29.333l6.84-1.78A13.31 13.31 0 0 0 16 29.333C23.364 29.333 29.333 23.364 29.333 16S23.364 2.667 16 2.667Zm7.74 18.86c-.328.92-1.62 1.694-2.66 1.92-.71.15-1.638.27-4.762-1.024-3.996-1.654-6.57-5.71-6.77-5.974-.2-.264-1.6-2.124-1.6-4.05 0-1.928 1.012-2.872 1.37-3.26.296-.32.78-.464 1.246-.464.15 0 .286.008.41.014.358.016.538.036.774.602.296.71 1.006 2.464 1.094 2.644.09.18.18.426.054.69-.118.27-.224.382-.404.59-.18.21-.35.37-.53.6-.164.198-.348.41-.14.762.21.35.928 1.532 1.99 2.478 1.37 1.22 2.5 1.61 2.86 1.768.27.118.59.094.79-.118.252-.27.566-.722.886-1.166.226-.318.51-.358.812-.244.31.106 1.95.918 2.288 1.084.338.166.56.246.644.388.082.146.082.842-.244 1.62Z"
    />
  </svg>
);

export function ChannelBadge({ channelType, providerSlug }: ChannelBadgeProps) {
  if (!channelType) return null;

  // Detecta WhatsApp oficial (WABA) — restrito ao channel_type whatsapp/whatsapp_oficial
  const isWaba =
    channelType === 'whatsapp_oficial' ||
    (channelType === 'whatsapp' &&
      !!providerSlug &&
      /meta|waba|cloud/i.test(providerSlug));

  let inner: JSX.Element;
  if (isWaba) {
    inner = (
      <span className="text-foreground inline-flex">
        <WhatsAppGlyph color="currentColor" />
      </span>
    );
  } else if (channelType === 'whatsapp') {
    inner = <WhatsAppGlyph color="#25D366" />;
  } else if (channelType === 'instagram') {
    inner = <Instagram className="h-[12px] w-[12px] text-pink-500" strokeWidth={2.5} />;
  } else if (channelType === 'messenger') {
    inner = <MessagesSquare className="h-[12px] w-[12px] text-blue-500" strokeWidth={2.5} />;
  } else if (channelType === 'telegram') {
    inner = <Send className="h-[12px] w-[12px] text-sky-500" strokeWidth={2.5} />;
  } else if (channelType === 'webchat') {
    inner = <Globe className="h-[12px] w-[12px] text-muted-foreground" strokeWidth={2.5} />;
  } else {
    inner = <Inbox className="h-[12px] w-[12px] text-muted-foreground" strokeWidth={2.5} />;
  }

  return (
    <div
      className="absolute -bottom-1 -left-1 z-10 flex h-[16px] w-[16px] items-center justify-center rounded-full bg-background border border-border shadow-sm"
      aria-label={`Canal: ${channelType}`}
    >
      {inner}
    </div>
  );
}
